import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Button, Typography, TextField, Paper,
    Alert, Breadcrumbs, Link
} from '@mui/material';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { ApiServiceContext } from '../../../../api/ApiService.context';
import { QuoteExperienceService } from '../../../../api/quote-experience/QuoteExperienceService.class';
import { QuoteExperienceState, QEFile } from '../../QuoteExperienceQuickSetup.page';
import {
    generateId, sanitizeName, buildFileTree, hasFileInPath,
    stripBundleRoot, isFileByName,
    ALLOWED_FILE_ACCEPT, ALLOWED_FILE_MIME_TYPES, ALLOWED_FILE_EXTENSIONS_LABEL
} from '../../utils/fileUtils';
import { FileTreeItem } from './FileTreeItem';

export const FileManagerPanel: FC<{
    bundleGuid: string | undefined;
    current: Partial<QuoteExperienceState>;
    updateData: (pointer: string, value: any, setInitial?: boolean) => void;
    setIsLoading: (key: string, value: boolean) => void;
}> = ({ bundleGuid, current, updateData, setIsLoading }) => {
    const { getApiServiceCollections } = useContext(ApiServiceContext);
    const { QuoteExperienceService } = getApiServiceCollections() as {
        QuoteExperienceService: QuoteExperienceService;
    };

    const [existingFiles, setExistingFiles] = useState<string[]>([]);
    const [virtualFolders, setVirtualFolders] = useState<string[]>([]);
    const [loadError, setLoadError] = useState<string>('');
    const [newFolderName, setNewFolderName] = useState<string>('');
    const [newFolderParent, setNewFolderParent] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadTargetFolder, setUploadTargetFolder] = useState<string>('');
    const [fileTypeError, setFileTypeError] = useState<string>('');

    const pendingFiles = useMemo(() => current.pendingFiles || [], [current.pendingFiles]);


    useEffect(() => {
        if (!bundleGuid) return;
        let active = true;

        const loadFiles = async (basePath: string): Promise<string[]> => {
            try {
                const listEndpoint = QuoteExperienceService.listAssetFiles;
                const resp = await listEndpoint({ bundleGuid, folderPath: basePath });
                const items: string[] = resp.jsonBody ?? [];
                const results: string[] = [];
                for (const item of items) {
                    const fullPath = basePath ? `${basePath}/${item}` : item;
                    if (isFileByName(item)) {
                        results.push(fullPath);
                    } else {
                        results.push(...await loadFiles(fullPath));
                    }
                }
                return results;
            } catch {
                return [];
            }
        };

        const init = async () => {
            setIsLoading('FileManager', true);
            setLoadError('');
            try {
                const allFiles = await loadFiles('');
                if (active) setExistingFiles(stripBundleRoot(allFiles));
            } catch {
                if (active) setLoadError('Failed to load files');
            } finally {
                if (active) setIsLoading('FileManager', false);
            }
        };

        init();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bundleGuid]);


    const tree = useMemo(
        () => buildFileTree(existingFiles, pendingFiles, virtualFolders),
        [existingFiles, pendingFiles, virtualFolders]
    );

    const emptyVirtualFolders = useMemo(
        () => virtualFolders.filter(f => !hasFileInPath(f, existingFiles, pendingFiles)),
        [virtualFolders, existingFiles, pendingFiles]
    );

    const fileCount = existingFiles.length + pendingFiles.length;

    const handleAddFolder = useCallback((parentPath: string) => {
        setNewFolderParent(parentPath);
        setNewFolderName('');
    }, []);

    const handleConfirmNewFolder = useCallback(() => {
        const name = sanitizeName(newFolderName);
        if (!name) return;
        const folderPath = newFolderParent ? `${newFolderParent}/${name}` : name;
        if (!virtualFolders.includes(folderPath)) {
            setVirtualFolders(prev => [...prev, folderPath]);
        }
        setNewFolderParent(null);
        setNewFolderName('');
    }, [newFolderName, newFolderParent, virtualFolders]);

    const handleCancelNewFolder = useCallback(() => {
        setNewFolderParent(null);
        setNewFolderName('');
    }, []);

    const handleRemoveVirtualFolder = useCallback((path: string) => {
        setVirtualFolders(prev => prev.filter(f => f !== path));
    }, []);

    const handleAddFile = useCallback((folderPath: string) => {
        setUploadTargetFolder(folderPath);
        setFileTypeError('');
        fileInputRef.current?.click();
    }, []);

    const handleFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_FILE_MIME_TYPES.includes(file.type)) {
            setFileTypeError(`"${file.name}" is not a supported file type. Allowed: ${ALLOWED_FILE_EXTENSIONS_LABEL}`);
            e.target.value = '';
            return;
        }

        setFileTypeError('');
        const fileName = sanitizeName(file.name) || file.name;
        const filePath = uploadTargetFolder ? `${uploadTargetFolder}/${fileName}` : fileName;

        updateData('/pendingFiles', [...pendingFiles, {
            id: generateId(),
            file,
            name: fileName,
            path: filePath,
            error: null,
        } as QEFile]);

        if (uploadTargetFolder && virtualFolders.includes(uploadTargetFolder)) {
            setVirtualFolders(prev => prev.filter(f => f !== uploadTargetFolder));
        }

        e.target.value = '';
    }, [uploadTargetFolder, pendingFiles, updateData, virtualFolders]);

    const handleRemovePendingFile = useCallback((path: string) => {
        updateData('/pendingFiles', pendingFiles.filter(f => f.path !== path));
    }, [pendingFiles, updateData]);


    if (!bundleGuid) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    Save this Quote Experience to use the File Manager.
                </Typography>
            </Box>
        );
    }

    const treeCallbacks = {
        onAddFile: handleAddFile,
        onAddFolder: handleAddFolder,
        onRemovePendingFile: handleRemovePendingFile,
        onRemoveVirtualFolder: handleRemoveVirtualFolder,
    };

    return (
        <Box>
            {loadError && (
                <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
            )}

            {fileTypeError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFileTypeError('')}>
                    {fileTypeError}
                </Alert>
            )}

            {emptyVirtualFolders.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You have unsaved folders. These cannot be saved unless a file is added inside of them.
                </Alert>
            )}

            <input
                type="file"
                accept={ALLOWED_FILE_ACCEPT}
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelected}
            />

            {newFolderParent !== null && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        New folder in:
                    </Typography>
                    <Breadcrumbs separator="/" sx={{ mb: 1, fontSize: 12 }}>
                        <Link
                            component="button"
                            underline="hover"
                            color="text.secondary"
                            sx={{ fontSize: 12, fontFamily: 'monospace' }}
                            onClick={() => setNewFolderParent('')}
                        >
                            /
                        </Link>
                        {newFolderParent.split('/').filter(Boolean).map((part, i, arr) => (
                            <Typography key={i} variant="caption" sx={{ fontFamily: 'monospace', fontSize: 12 }}
                                color={i === arr.length - 1 ? 'text.primary' : 'text.secondary'}
                            >
                                {part}
                            </Typography>
                        ))}
                    </Breadcrumbs>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                            size="small"
                            placeholder="Folder name"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleConfirmNewFolder();
                                if (e.key === 'Escape') handleCancelNewFolder();
                            }}
                            autoFocus
                            sx={{ '& .MuiInputBase-input': { fontSize: 13, fontFamily: 'monospace' } }}
                        />
                        <Button size="small" variant="contained" onClick={handleConfirmNewFolder} disabled={!newFolderName.trim()}>
                            Create
                        </Button>
                        <Button size="small" onClick={handleCancelNewFolder}>
                            Cancel
                        </Button>
                    </Box>
                </Paper>
            )}

            <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 1.5, minHeight: 120, fontFamily: 'monospace' }}>
                {tree.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                        No files found. Use the buttons below to add folders and files.
                    </Typography>
                ) : (
                    tree.map(node => (
                        <FileTreeItem key={node.fullPath} node={node} depth={0} {...treeCallbacks} />
                    ))
                )}
            </Paper>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" size="small" startIcon={<CreateNewFolderIcon />}
                    onClick={() => handleAddFolder('')}
                >
                    New Folder
                </Button>
                <Button variant="outlined" size="small" startIcon={<NoteAddIcon />}
                    onClick={() => handleAddFile('')}
                >
                    Add File
                </Button>
                <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                    {fileCount} file(s) &middot; Accepted: {ALLOWED_FILE_EXTENSIONS_LABEL}
                </Typography>
            </Box>

            {pendingFiles.length > 0 && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                    {pendingFiles.length} file(s) pending upload &mdash; will be saved on publish.
                </Typography>
            )}
        </Box>
    );
};
